import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Spin } from 'antd';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { SESSION_EXPIRED_EVENT, get_base_url } from '@/shared/http/client';
import { clearCachedAvailableProviders } from '@/app/providers/globalState';
import { readCachedAuthConfig, writeCachedAuthConfig } from './authConfigCache';

const AuthContext = createContext(null);

/** Where `POST /users/bootstrap` strangers (`state: "no_access"`) are sent on SaaS. */
export const WAITLIST_APPLY_PATH = '/waitlist/apply';

/**
 * Where strangers are sent on a self-hosted install, which has no waiting
 * list: a "no access" page that only offers "Log out" (a workspace admin has
 * to invite them).
 */
export const NO_ACCESS_PATH = '/request-access';

/** Where a self-hosted Unassigned member waits for an admin. Full-page, outside the shell. */
export const PENDING_APPROVAL_PATH = '/pending-approval';

/**
 * Pick the landing for a signed-in person with no workspace access.
 *
 * `waitlistEnabled` is the backend's `waitlist_enabled` (`IS_SAAS`) flag, so
 * the choice does not depend on the order in which `/auth/debug` and
 * `/users/bootstrap` resolve.  Anything but an explicit `true` keeps the
 * person out, mirroring the backend, which answers 404 on every waiting-list
 * route when `IS_SAAS` is off.
 */
export function resolveNoAccessPath(waitlistEnabled) {
    return waitlistEnabled === true ? WAITLIST_APPLY_PATH : NO_ACCESS_PATH;
}

/**
 * Full-page routes that render without the app shell. A missing session on
 * one of these stays put; everywhere else an unsigned session goes to /login
 * before the shell can mount.
 */
/** Email-link page. Renders signed out, and a signed-in visitor is not redirected away. */
export const JOIN_WORKSPACE_PATH = '/join-workspace';

const SIGNED_OUT_PAGES = ['/login', '/waitlist', NO_ACCESS_PATH, '/create-workspace', JOIN_WORKSPACE_PATH];

export function isSignedOutPage(pathname) {
    const path = pathname || '/';
    return SIGNED_OUT_PAGES.some((page) => path === page || path.startsWith(`${page}/`));
}

function getAuthBaseUrl() {
    const isProxy = window.location.port === '8760';
    return isProxy ? '/api' : get_base_url(window.location.origin);
}

/**
 * Query parameter of a deep link that names the workspace to open, e.g. the
 * button of an emailed billing notice: `/settings?tab=billing&workspace_id=<id>`.
 * Forwarded to `POST /users/bootstrap` on page load, which makes that
 * workspace active (when the person is a member of it) instead of the one
 * they used last.
 */
export const WORKSPACE_LINK_PARAM = 'workspace_id';

/** `POST /users/bootstrap` URL, carrying the deep link's workspace when the page has one. */
export function bootstrapUrl(base, search) {
    const url = `${base}/users/bootstrap`;
    const workspaceId = new URLSearchParams(search || '').get(WORKSPACE_LINK_PARAM);
    return workspaceId ? `${url}?${WORKSPACE_LINK_PARAM}=${encodeURIComponent(workspaceId)}` : url;
}

/**
 * Overlay the persisted profile from `POST /users/bootstrap` on the session
 * user: the stored (data-URL) picture wins over a provider URL, and the stored
 * display name wins so a name edited in the onboarding wizard survives
 * re-login.  Returns `null` when nothing needs to change.
 */
export function mergeBootstrapUser(sessionUser, bootUser) {
    if (!bootUser) return null;
    const base = sessionUser || {};
    const merged = { ...base };
    let changed = false;
    const persistedPic = bootUser.picture;
    if (persistedPic && (!base.picture || !String(base.picture).startsWith('data:'))) {
        merged.picture = persistedPic;
        changed = true;
    }
    const persistedName = typeof bootUser.name === 'string' ? bootUser.name.trim() : '';
    if (persistedName && persistedName !== base.name) {
        merged.name = persistedName;
        changed = true;
    }
    // Onboarding answers live only on the user document, so they always come
    // from the bootstrap (the IdP session knows nothing about them).
    const persistedCountry = typeof bootUser.country === 'string' ? bootUser.country.trim() : '';
    if (persistedCountry && persistedCountry !== base.country) {
        merged.country = persistedCountry;
        changed = true;
    }
    if (bootUser.onboarding && typeof bootUser.onboarding === 'object' && bootUser.onboarding !== base.onboarding) {
        merged.onboarding = bootUser.onboarding;
        changed = true;
    }
    if (typeof bootUser.picture_set_by_user === 'boolean' && bootUser.picture_set_by_user !== base.picture_set_by_user) {
        merged.picture_set_by_user = bootUser.picture_set_by_user;
        changed = true;
    }
    // Preferences (home view, walkthrough progress) are persisted per user
    // and only ever come from the bootstrap.
    if (bootUser.preferences && typeof bootUser.preferences === 'object' && bootUser.preferences !== base.preferences) {
        merged.preferences = bootUser.preferences;
        changed = true;
    }
    return changed ? merged : null;
}

/**
 * Whether the signed-in person may open a workspace of their own.
 *
 * `POST /users/bootstrap` answers `can_create_workspace` (gods, creators and
 * every member except one whose Creator approval a God revoked or one still
 * pending approval — the same rule `POST /workspaces` enforces).  An older
 * backend without the field falls back to the pre-4.15 role rule so the
 * "Add New Workspace" entry and the no-workspace routing never regress for
 * creators and gods.
 */
export function resolveCanCreateWorkspace(boot, platformRoleValue) {
    if (typeof boot?.can_create_workspace === 'boolean') return boot.can_create_workspace;
    return platformRoleValue === 'god' || platformRoleValue === 'creator';
}

/** Centered spinner/error in the main content area (nav stays visible). */
function AuthGateContent({ children }) {
    return (
        <div
            style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
            }}
        >
            {children}
        </div>
    );
}

function AuthLoadingContent() {
    return (
        <AuthGateContent>
            <div
                style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '50vh',
                }}
            >
                <Spin size="large" />
            </div>
        </AuthGateContent>
    );
}

function AuthErrorContent({ message }) {
    return (
        <AuthGateContent>
            <div
                style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 24,
                    minHeight: '50vh',
                }}
            >
                <Alert
                    type="error"
                    showIcon
                    message="Could not verify authentication"
                    description={message}
                    style={{ maxWidth: 480, width: '100%' }}
                />
            </div>
        </AuthGateContent>
    );
}

async function readAuthErrorMessage(response, fallback) {
    try {
        const data = await response.json();
        return data?.detail || data?.message || fallback;
    } catch (_) {
        return fallback;
    }
}

/** Message shown when `POST /users/bootstrap` fails for a signed-in user. */
export const BOOTSTRAP_FAILED_MESSAGE = 'Could not load your account. Check your connection and try again.';

const BACKEND_UNREACHABLE_MESSAGE =
    'Cannot reach the backend. Check that the API is running (proxy on :8760/api or backend on :8000).';

/** The session identity fields `GET /auth/me` used to provide, taken from the bootstrap user. */
function pickSessionUser({ sub, email, name, picture }) {
    return { sub, email, name, picture };
}

/** `GET /auth/me`'s user, or `null` when there is no session. */
async function fetchSessionUser(base) {
    let resp;
    try {
        resp = await fetch(`${base}/auth/me`, {
            method: 'GET',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
        });
    } catch (_) {
        throw new Error(BACKEND_UNREACHABLE_MESSAGE);
    }
    if (resp.status === 401) return null;
    if (!resp.ok) {
        throw new Error(await readAuthErrorMessage(resp, `Session check failed (${resp.status}).`));
    }
    const json = await resp.json();
    return json?.user || null;
}

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    // `false` while a full `bootstrapAuth` is in flight (the first one and
    // every `retryAuth`).  `loading` also flips on every later `refresh()`,
    // so the full-screen boundary (`AuthBoundary`) keys off this flag instead
    // and only the in-shell `AuthGate` spinner shows for mid-session refreshes.
    const [initialized, setInitialized] = useState(false);
    const [authError, setAuthError] = useState(null);
    // Client-side redirect requested by the bootstrap (stranger → waiting
    // list, creator → onboarding wizard, ...).  Consumed by the effect below
    // through the router instead of `window.location.href`, so the shell is
    // never mounted for a page the person is about to leave and nothing
    // flashes before the redirect.
    const [redirectTo, setRedirectTo] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    // `bootstrapUserSession` is a zero-dependency callback that resolves long
    // after it was created, so it reads the current path through a ref.
    const pathRef = useRef(location.pathname);
    // The whole router location: its query string at page load feeds
    // `bootstrapUrl`, and an expired session hands it to /login as `from`.
    const locationRef = useRef(location);
    useEffect(() => {
        pathRef.current = location.pathname;
        locationRef.current = location;
    }, [location]);

    /** Where /login sends the person back to after they sign in again (see `Login.jsx` `nextPath`). */
    const returnHere = useCallback(() => {
        const { pathname, search, hash } = locationRef.current;
        return { from: { pathname, search, hash } };
    }, []);

    const requestRedirect = useCallback((path, state) => {
        if (pathRef.current === path) return;
        setRedirectTo({ path, state });
    }, []);

    useEffect(() => {
        if (!redirectTo) return;
        if (location.pathname !== redirectTo.path) {
            // react-router applies navigations inside `startTransition`, so a
            // sync `setRedirectTo(null)` issued here would commit *before* the
            // location changes and let the old page (the shell) render once.
            // Keep the redirect pending until the router reports the new
            // path; this effect re-runs on `location.pathname` and clears it.
            navigate(redirectTo.path, { replace: true, state: redirectTo.state });
            return;
        }
        setRedirectTo(null);
    }, [redirectTo, location.pathname, navigate]);
    // Last `GET /auth/debug` answer (cached or fresh); the login page reads its providers.
    const [authConfig, setAuthConfig] = useState(null);
    const [authMethod, setAuthMethod] = useState('GOOGLE');
    const [role, setRole] = useState('');
    // Platform-wide role from `POST /users/bootstrap` (god / creator / member).
    // Gates god-only UI such as the Waiting List tab; auth-disabled installs
    // behave as god, mirroring the backend's `require_god` bypass.
    const [platformRole, setPlatformRole] = useState('member');
    // `can_create_workspace` from the bootstrap (see resolveCanCreateWorkspace):
    // gates "Add New Workspace" and where a workspace-less person lands.
    const [canCreateWorkspace, setCanCreateWorkspace] = useState(false);
    const [isAuthEnabled, setIsAuthEnabled] = useState(false);

    // An expired session comes back to the page it was on (an emailed deep
    // link included) once the person signs in again.
    const redirectToLogin = useCallback(() => {
        setUser(null);
        setAuthError(null);
        requestRedirect('/login', returnHere());
    }, [requestRedirect, returnHere]);

    // The session cookie is httpOnly, but /auth/me already returns its `exp`.
    // One local timer plus a visibility/focus clock check sends an already
    // expired tab to /login without another request. A 401 from any other
    // authenticated call (revocation before `exp`) does the same.
    useEffect(() => {
        const exp = user?.exp;
        if (typeof exp !== 'number' || !Number.isFinite(exp)) return undefined;
        const redirectIfExpired = () => {
            if (Date.now() < exp * 1000) return;
            redirectToLogin();
        };
        const delay = exp * 1000 - Date.now();
        const timer = delay <= 0
            ? null
            : window.setTimeout(redirectIfExpired, Math.min(delay, 2147483647));
        const onVisible = () => {
            if (document.visibilityState === 'visible') redirectIfExpired();
        };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', redirectIfExpired);
        if (delay <= 0) redirectIfExpired();
        return () => {
            if (timer != null) window.clearTimeout(timer);
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', redirectIfExpired);
        };
    }, [user?.exp, redirectToLogin]);

    useEffect(() => {
        if (!isAuthEnabled) return undefined;
        const onSessionExpired = () => redirectToLogin();
        window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
        return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    }, [isAuthEnabled, redirectToLogin]);

    // SaaS billing flags from GET /auth/debug. Default to "self-hosted, no
    // billing" so the Plans & Billing tab (and Stripe.js) never appears
    // unless the backend explicitly opts in.
    const [isSaas, setIsSaas] = useState(false);
    // `bootstrapUserSession` is a zero-dependency callback, so it reads the
    // SaaS flag through a ref rather than the state above.
    const isSaasRef = useRef(false);
    const [billingEnabled, setBillingEnabled] = useState(false);
    const [stripePublishableKey, setStripePublishableKey] = useState(null);
    // "Give us Feedback" (avatar menu → ClickUp task) is only offered when the
    // backend has ClickUp credentials; self-hosted installs never see it.
    const [feedbackEnabled, setFeedbackEnabled] = useState(false);
    // SaaS gating: "Contact us" in the disabled task-type / autonomy tooltips
    // opens the customer-intents modal only when the backend can file the
    // request to ClickUp; otherwise the tooltip is plain text.
    const [customerIntentsEnabled, setCustomerIntentsEnabled] = useState(false);
    // The MCP server (/api/mcp) is on unless an install sets MCP_ENABLED
    // "false"; its docs page is hidden where the endpoint is a 404.
    const [mcpEnabled, setMcpEnabled] = useState(false);
    // Phase 4: active workspace and membership list.
    const [workspace, setWorkspace] = useState(null);
    const [workspaces, setWorkspaces] = useState([]);
    // Billing plan of the active workspace ("free" | "pro"), from
    // ``/users/bootstrap``; ``null`` when billing is off or still unknown.
    // Billing is per workspace (one Stripe customer each), so the tag is the
    // same for every member of that workspace and changes on workspace switch.
    const [plan, setPlan] = useState(null);
    // Free-usage snapshot of the active workspace from ``/users/bootstrap``:
    // ``{limit_cents, used_cents, remaining_cents, exhausted}``; ``null`` when
    // billing is off, still unknown, or the backend predates the field.
    const [freeUsage, setFreeUsage] = useState(null);
    // Whether auto-recharge is on for the active workspace, from
    // ``/users/bootstrap`` ``auto_charge_enabled``; ``null`` when unknown.
    // Lets non-admins (who cannot read ``billing/profile``) pick the right
    // out-of-credits banner variant.
    const [autoChargeEnabled, setAutoChargeEnabled] = useState(null);

    useEffect(() => {
        if (!window.FS || !user) {
            return;
        }

        window.FS('setIdentity', {
            uid: user?.sub || 'no sub',
            properties: {
                display_name: user?.name,
            },
        });
    }, [user]);

    /**
     * Where a signed-in person without an active workspace goes: gods to the
     * console, anyone allowed to create a workspace (creators and ordinary
     * members alike) to the onboarding wizard, and the rest — an account
     * whose Creator approval was revoked — to the waiting list (SaaS) or the
     * "no access" page (self-hosted).
     */
    const routeWithoutWorkspace = useCallback((platformRoleValue, canCreate) => {
        if (platformRoleValue === 'god') {
            requestRedirect('/admin');
        } else if (canCreate) {
            requestRedirect('/create-workspace');
        } else {
            requestRedirect(resolveNoAccessPath(isSaasRef.current));
        }
    }, [requestRedirect]);

    const bootstrapUserSession = useCallback(async (base, search = '') => {
        let resp;
        try {
            resp = await fetch(bootstrapUrl(base, search), { method: 'POST', credentials: 'include' });
        } catch (_) {
            throw new Error(BOOTSTRAP_FAILED_MESSAGE);
        }
        if (resp.status === 401) {
            // No session (never signed in, or it expired). Hold the boundary
            // loader until /login is the current route so the shell never
            // mounts for a dead cookie; public pages (login, waitlist) stay.
            // The page (with its query string, e.g. an emailed billing link)
            // rides along so sign-in returns here instead of the home page.
            setUser(null);
            setAuthError(null);
            if (!isSignedOutPage(pathRef.current)) requestRedirect('/login', returnHere());
            return;
        }
        if (!resp.ok) {
            // A failed bootstrap used to be swallowed, leaving the shell on an
            // endless spinner.  Surface it so `AuthBoundary` can offer Retry.
            throw new Error(await readAuthErrorMessage(resp, BOOTSTRAP_FAILED_MESSAGE));
        }
        const boot = await resp.json().catch(() => null);
        if (!boot || typeof boot !== 'object') {
            throw new Error(BOOTSTRAP_FAILED_MESSAGE);
        }
        // The bootstrap user carries the session identity (`sub`); only a
        // stranger (`no_access`, no user) or an older backend needs /auth/me.
        const sessionUser = boot.user?.sub ? pickSessionUser(boot.user) : await fetchSessionUser(base);
        if (!sessionUser) {
            setUser(null);
            return;
        }
        setUser(sessionUser);

        // Phase 3: stranger gate — the person authenticated with the IdP
        // but has no invitation and no approved Creator request on this
        // platform.  The OAuth callback already lands them on the waiting
        // list; this is the fallback for a stranger who typed another URL.
        // SaaS sends them to the waiting-list questionnaire; a self-hosted
        // install shows the "no access" page instead.
        // The join page has to stay put: a pending invitee may be `no_access`
        // (creation blocked until they accept) or `no_workspace` (the account
        // exists, the membership does not). Redirecting would drop the token.
        const stayOnJoinPage = pathRef.current === JOIN_WORKSPACE_PATH;

        if (boot.state === 'no_access') {
            setPlatformRole('member');
            setCanCreateWorkspace(false);
            const waitlistEnabled = typeof boot.waitlist_enabled === 'boolean'
                ? boot.waitlist_enabled
                : isSaasRef.current;
            if (!stayOnJoinPage) requestRedirect(resolveNoAccessPath(waitlistEnabled));
            return;
        }

        // Phase 4: no workspace — route by platform role.
        if (boot.state === 'no_workspace') {
            const pr = boot.platform_role || 'member';
            const canCreate = resolveCanCreateWorkspace(boot, pr);
            setPlatformRole(pr);
            setCanCreateWorkspace(canCreate);
            if (!stayOnJoinPage) routeWithoutWorkspace(pr, canCreate);
            const mergedUser = mergeBootstrapUser(sessionUser, boot.user);
            if (mergedUser) setUser(mergedUser);
            return;
        }

        // Onboarding in progress — a Creator started the /create-workspace
        // wizard (saved at least one step) but has not accepted the terms
        // yet.  They may already own a workspace, so keep the list around
        // for the wizard to resume on the right step.
        if (boot.state === 'onboarding') {
            const pr = boot.platform_role || 'creator';
            setPlatformRole(pr);
            setCanCreateWorkspace(resolveCanCreateWorkspace(boot, pr));
            if (boot.workspaces) setWorkspaces(boot.workspaces);
            const mergedUser = mergeBootstrapUser(sessionUser, boot.user);
            if (mergedUser) setUser(mergedUser);
            if (pathRef.current !== JOIN_WORKSPACE_PATH) requestRedirect('/create-workspace');
            return;
        }

        // Phase 4: state=ok — active workspace resolved.
        const bootRole = boot.role;
        if (bootRole) setRole(bootRole);
        const pr = boot.platform_role || boot.user?.platform_role || 'member';
        const canCreate = resolveCanCreateWorkspace(boot, pr);
        setPlatformRole(pr);
        setCanCreateWorkspace(canCreate);
        const mergedUser = mergeBootstrapUser(sessionUser, boot.user);
        if (mergedUser) setUser(mergedUser);
        if (boot.workspaces) setWorkspaces(boot.workspaces);
        setPlan(boot.plan === 'free' || boot.plan === 'pro' ? boot.plan : null);
        setFreeUsage(boot.free_usage && typeof boot.free_usage === 'object' ? boot.free_usage : null);
        setAutoChargeEnabled(typeof boot.auto_charge_enabled === 'boolean' ? boot.auto_charge_enabled : null);

        if (!boot.workspace) {
            // Defensive: an `ok` answer without a workspace (an older backend
            // whose last workspace was deleted) must not strand the person on
            // the shell's spinner — route them exactly like `no_workspace`.
            if (pathRef.current !== JOIN_WORKSPACE_PATH) routeWithoutWorkspace(pr, canCreate);
            return;
        }
        setWorkspace(boot.workspace);

        const currentPath = pathRef.current;
        // Legacy: Unassigned members on the migrated workspace still
        // land on /pending-approval until their role is promoted.
        // The join page is exempt so an invite link is not discarded.
        const isUnassigned = bootRole === 'Unassigned';
        if (isUnassigned && currentPath !== JOIN_WORKSPACE_PATH) {
            requestRedirect(PENDING_APPROVAL_PATH);
        } else if (
            currentPath === PENDING_APPROVAL_PATH
            || currentPath === WAITLIST_APPLY_PATH
            || currentPath === NO_ACCESS_PATH
            || currentPath === '/create-workspace'
        ) {
            // User now has an active workspace (invitation consumed,
            // Creator request approved, or workspace just created);
            // redirect home.
            requestRedirect('/');
        }
    }, [requestRedirect, returnHere, routeWithoutWorkspace]);

    /**
     * Auth disabled: the backend treats every request as the blank-e-mail
     * guest and `require_admin` waives the role check, so the identity stays
     * "Guest / Admin / god" no matter what the bootstrap answers.  The
     * bootstrap still runs so the workspace switcher gets the guest's
     * workspaces and active workspace (the landing rule, or the default
     * workspace the first-user path creates).  Any answer but `ok` — no
     * membership yet, the stranger gate on a database that already has real
     * users, a backend hiccup — keeps the workspace-less guest shell instead
     * of redirecting the way a signed-in person would be.
     */
    const bootstrapGuestSession = useCallback(async (base, search = '') => {
        setUser({ name: 'Guest', email: '' });
        setRole('Admin');
        setPlatformRole('god');
        setCanCreateWorkspace(true);
        let boot = null;
        try {
            const resp = await fetch(bootstrapUrl(base, search), { method: 'POST', credentials: 'include' });
            boot = resp.ok ? await resp.json().catch(() => null) : null;
        } catch (_) {
            boot = null;
        }
        // The guest identity is fixed, but its persisted preferences
        // (walkthrough progress, home view) still come from the user document.
        const guestPreferences = boot?.user?.preferences;
        if (guestPreferences && typeof guestPreferences === 'object') {
            setUser((prev) => ({ ...(prev || { name: 'Guest', email: '' }), preferences: guestPreferences }));
        }
        if (boot?.state !== 'ok' || !boot.workspace) return;
        setWorkspaces(Array.isArray(boot.workspaces) ? boot.workspaces : []);
        setWorkspace(boot.workspace);
        setPlan(boot.plan === 'free' || boot.plan === 'pro' ? boot.plan : null);
        setFreeUsage(boot.free_usage && typeof boot.free_usage === 'object' ? boot.free_usage : null);
        setAutoChargeEnabled(typeof boot.auto_charge_enabled === 'boolean' ? boot.auto_charge_enabled : null);
    }, []);

    const applyAuthConfig = useCallback((info) => {
        setAuthConfig(info);
        setAuthMethod((info?.auth_method || 'GOOGLE').toUpperCase());
        setIsAuthEnabled(info?.auth_enabled);
        const saas = info?.is_saas === true;
        isSaasRef.current = saas;
        setIsSaas(saas);
        setBillingEnabled(info?.billing_enabled === true);
        setStripePublishableKey(info?.stripe_publishable_key || null);
        setFeedbackEnabled(info?.feedback_enabled === true);
        setCustomerIntentsEnabled(info?.customer_intents_enabled === true);
        setMcpEnabled(info?.mcp_enabled === true);
    }, []);

    /** `GET /auth/debug`, applied to state and written to the cache. */
    const fetchAuthConfig = useCallback(async (base) => {
        let res;
        try {
            res = await fetch(`${base}/auth/debug`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
        } catch (_) {
            throw new Error(BACKEND_UNREACHABLE_MESSAGE);
        }
        if (!res.ok) {
            throw new Error(await readAuthErrorMessage(res, `Auth configuration check failed (${res.status}).`));
        }
        const info = await res.json();
        writeCachedAuthConfig(info);
        applyAuthConfig(info);
        return info;
    }, [applyAuthConfig]);

    const bootstrapAuth = useCallback(async () => {
        setLoading(true);
        // Drop back to "not initialized" so a retry from the error screen keeps
        // the neutral boundary loader up instead of mounting the route table
        // (and its fetches) while the bootstrap is still running.
        setInitialized(false);
        setAuthError(null);
        const base = getAuthBaseUrl();
        // Only the page load honours a deep link's workspace; a mid-session
        // `refresh()` keeps whatever workspace is active by then.
        const { search } = locationRef.current;
        try {
            let info = readCachedAuthConfig();
            if (info) {
                applyAuthConfig(info);
                // Refresh in the background; a failure keeps the cached flags.
                fetchAuthConfig(base).catch(() => {});
            } else {
                info = await fetchAuthConfig(base);
            }
            const method = (info?.auth_method || 'GOOGLE').toUpperCase();
            if (method === 'NONE' || info?.auth_enabled === false) {
                await bootstrapGuestSession(base, search);
                return;
            }
            await bootstrapUserSession(base, search);
        } catch (error) {
            setAuthError(error?.message || 'Authentication check failed.');
            setUser(null);
        } finally {
            setLoading(false);
            setInitialized(true);
        }
    }, [applyAuthConfig, fetchAuthConfig, bootstrapGuestSession, bootstrapUserSession]);

    const refresh = useCallback(() => {
        setLoading(true);
        setAuthError(null);
        bootstrapUserSession(getAuthBaseUrl())
            .catch((error) => {
                if (error?.message) {
                    setAuthError(error.message);
                }
                setUser(null);
            })
            .finally(() => setLoading(false));
    }, [bootstrapUserSession]);

    useEffect(() => {
        bootstrapAuth();
    }, [bootstrapAuth]);

    const loginWithGoogle = useCallback((nextPath) => {
        const base = getAuthBaseUrl();
        let loginUrl = `${base}/auth/google/login`;
        if (nextPath && typeof nextPath === 'string' && nextPath.startsWith('/') && !nextPath.startsWith('//')) {
            loginUrl += `?next=${encodeURIComponent(nextPath)}`;
        }
        window.location.href = loginUrl;
    }, []);

    const logout = useCallback(async () => {
        const base = get_base_url(window.location.origin);
        try {
            await fetch(`${base}/auth/logout`, { method: 'POST', credentials: 'include' });
        } finally {
            // Even when the backend is unreachable the local session state is
            // stale: drop it (and any auth error) so /login renders cleanly.
            clearCachedAvailableProviders();
            setUser(null);
            setPlatformRole('member');
            setCanCreateWorkspace(false);
            setAuthError(null);
            // Hand the hop to the redirect effect so the boundary keeps its
            // loader up until /login is the current route; a plain
            // `navigate()` from the caller is a transition and would let the
            // route table render the current page once in between.
            requestRedirect('/login');
        }
    }, [requestRedirect]);

    // Phase 4: switch the active workspace (server re-issues cookie + persists last_workspace_id).
    const switchWorkspace = useCallback(async (workspaceId) => {
        const base = getAuthBaseUrl();
        const resp = await fetch(`${base}/workspaces/${workspaceId}/switch`, {
            method: 'POST',
            credentials: 'include',
        });
        if (!resp.ok) {
            const body = await resp.json().catch(() => ({}));
            throw new Error(body?.detail || 'Failed to switch workspace');
        }
        const data = await resp.json().catch(() => ({}));
        if (data?.workspace) setWorkspace(data.workspace);
        if (data?.role) setRole(data.role);
        return data;
    }, []);

    /**
     * Merge a ``PATCH /workspaces/{id}`` response into the session so the
     * header, avatar menu and workspace list reflect a renamed workspace or a
     * new logo without a reload.  Only the active workspace is replaced
     * wholesale; the membership list just picks up ``name``/``logo``.
     */
    const applyWorkspaceUpdate = useCallback((updated) => {
        if (!updated || !updated.id) return;
        setWorkspace((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
        setWorkspaces((prev) => prev.map((item) => {
            if (item.id !== updated.id) return item;
            const next = { ...item };
            if (updated.name !== undefined) next.name = updated.name;
            if (updated.logo !== undefined) next.logo = updated.logo;
            if (updated.is_owner !== undefined) next.is_owner = updated.is_owner;
            return next;
        }));
    }, []);

    /**
     * Merge a ``POST /users/set-profile`` result into the session user so the
     * avatar menu and Settings > Profile show the new name / picture without a
     * reload.  ``set-profile`` echoes the name but not the (large) picture
     * data URL, so callers pass the picture they just uploaded.
     */
    const applyUserUpdate = useCallback((updated) => {
        if (!updated || typeof updated !== 'object') return;
        setUser((prev) => (prev ? { ...prev, ...updated } : prev));
    }, []);

    const redirectPending = redirectTo !== null;

    const value = useMemo(
        () => ({
            user,
            loading,
            initialized,
            redirectPending,
            retryAuth: bootstrapAuth,
            authError,
            authConfig,
            authMethod,
            role,
            platformRole,
            canCreateWorkspace,
            isAuthEnabled,
            isSaas,
            billingEnabled,
            stripePublishableKey,
            feedbackEnabled,
            customerIntentsEnabled,
            mcpEnabled,
            workspace,
            workspaces,
            plan,
            freeUsage,
            autoChargeEnabled,
            switchWorkspace,
            applyWorkspaceUpdate,
            applyUserUpdate,
            refresh,
            loginWithGoogle,
            logout,
        }),
        [
            user,
            loading,
            initialized,
            redirectPending,
            bootstrapAuth,
            authError,
            authConfig,
            authMethod,
            role,
            platformRole,
            canCreateWorkspace,
            isAuthEnabled,
            isSaas,
            billingEnabled,
            stripePublishableKey,
            feedbackEnabled,
            customerIntentsEnabled,
            mcpEnabled,
            workspace,
            workspaces,
            plan,
            freeUsage,
            autoChargeEnabled,
            switchWorkspace,
            applyWorkspaceUpdate,
            applyUserUpdate,
            refresh,
            loginWithGoogle,
            logout,
        ]
    );
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export const AuthGate = ({ children }) => {
    const { user, loading, authError, isAuthEnabled, role } = useAuth();
    const location = useLocation();
    if (loading) return <AuthLoadingContent />;
    if (authError) return <AuthErrorContent message={authError} />;
    if (!user && isAuthEnabled) return <Navigate to="/login" replace state={{ from: location }} />;
    if (isAuthEnabled && role === 'Unassigned' && location.pathname !== PENDING_APPROVAL_PATH) {
        return <Navigate to={PENDING_APPROVAL_PATH} replace state={{ from: location }} />;
    }
    return children;
};

export const RequireAdmin = ({ children }) => {
    const { loading, authError, role, isAuthEnabled } = useAuth();
    const location = useLocation();
    if (loading) return null;
    if (authError) {
        return <AuthErrorContent message={authError} />;
    }
    if (!isAuthEnabled) return children;
    if (role === 'Admin') return children;
    return <Navigate to="/" replace state={{ from: location }} />;
};

// Phase 4: guards routes that only Gods (platform-level super-admins) may access.
export const RequireGod = ({ children }) => {
    const { loading, authError, platformRole, isAuthEnabled } = useAuth();
    const location = useLocation();
    if (loading) return null;
    if (authError) {
        return <AuthErrorContent message={authError} />;
    }
    if (!isAuthEnabled) return children;
    if (platformRole === 'god') return children;
    return <Navigate to="/" replace state={{ from: location }} />;
};

/**
 * Guards the waiting-list routes (`/waitlist`, `/waitlist/apply`): they only
 * exist on SaaS, so a self-hosted install sends the visitor to `/login`
 * instead of rendering a form whose backend answers 404.
 */
export const RequireSaas = ({ children }) => {
    const { loading, authError, isSaas } = useAuth();
    if (loading) return <AuthLoadingContent />;
    if (authError) return <AuthErrorContent message={authError} />;
    if (isSaas !== true) return <Navigate to="/login" replace />;
    return children;
};
